import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import moment from "moment";

export default function CashPrintReports(props) {
  const [reportData, setReportData] = useState({
    inflowsData: [],
    receivablesData: [],
    outflowsData: [],
    expensesData: [],
    taxLiability: 0,
    criteria: [],
    dynamicInvMessage: "",
  });

  useEffect(() => {
    if (props.location && props.location.state) {
      const { data, criteria, dynamicInvMessage, taxLiability } = props.location.state;

      // Separate data into categories
      const inflows = data.filter(
        (item) => item.id && item.id.startsWith("inflow")
      );
      const receivables = data.filter(
        (item) => item.id && item.id.startsWith("receivable")
      );
      const outflows = data.filter(
        (item) => item.id && item.id.startsWith("outflow")
      );
      const expenses = data.filter(
        (item) => item.id && item.id.startsWith("expense")
      );

      setReportData({
        inflowsData: inflows,
        receivablesData: receivables,
        outflowsData: outflows,
        expensesData: expenses,
        taxLiability: taxLiability || 0,
        criteria: criteria || [],
        dynamicInvMessage: dynamicInvMessage || "Cash Projection Report",
      });
    }
  }, [props.location]);

  const handlePrint = () => {
    window.print();
  };

  const handleGoBack = () => {
    if (
      props.location &&
      props.location.state &&
      props.location.state.goBackUrl
    ) {
      props.history.push(props.location.state.goBackUrl);
    } else {
      props.history.goBack();
    }
  };

const formatNumber = (num) => {
  if (num === null || num === undefined || num === "") return "";
  
  const absoluteValue = Math.abs(num);
  const formattedValue = absoluteValue.toLocaleString("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  
  const CRDR = num < 0 ? " CR" : " DR";
  return formattedValue + CRDR;
};

  const calculateTotal = (dataArray) => {
    return dataArray
      .filter((item) => !item.isTotal)
      .reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const totalInflows = calculateTotal(reportData.inflowsData);
  const totalReceivables = calculateTotal(reportData.receivablesData);
  const totalOutflows = calculateTotal(reportData.outflowsData);
  const totalExpenses = calculateTotal(reportData.expensesData);
  const estimatedTaxLiability = reportData.taxLiability;

  const totalCashInflows = totalInflows + totalReceivables;
  const totalCashOutflows =
    totalOutflows + totalExpenses + estimatedTaxLiability;

  return (
    <div
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      {/* Header with buttons */}
      <div
        className="no-print"
        style={{
          padding: "20px",
          textAlign: "right",
          backgroundColor: "#f8f9fa",
        }}
      >
        <Button
          color="primary"
          onClick={handlePrint}
          style={{ marginRight: "10px" }}
        >
          Print Report
        </Button>
        <Button color="secondary" onClick={handleGoBack}>
          Go Back
        </Button>
      </div>

      {/* Main content */}
      <div
        className="print-container"
        style={{
          maxWidth: "100%",
          margin: "0 auto",
        }}
      >
        <div style={{ textAlign: "left", marginBottom: "10px" }}>
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            {reportData.dynamicInvMessage}
          </h2>
          {reportData.criteria.length > 0 && (
            <div style={{ fontSize: "14px", color: "#333" }}>
              {reportData.criteria.map((crit, idx) => (
                <span key={idx} style={{ marginRight: "15px" }}>
                  <strong>{crit.label}:</strong> {crit.value}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Main content table */}
        <table
          style={{
            width: "100%",
            fontSize: "14px",
          }}
        >
          <tbody>
            <tr>
              {/* INFLOWS Column */}
              <td
                style={{
                  width: "48%",
                  verticalAlign: "top",
                  paddingRight: "15px",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th
                        colSpan="2"
                        style={{
                          backgroundColor: "#f0f0f0",
                          padding: "6px 8px",
                          textAlign: "left",
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderBottom: "2px solid #000",
                        }}
                      >
                        INFLOWS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Bank Balances Section */}
                    <tr>
                      <td
                        colSpan="2"
                        style={{
                          fontWeight: "bold",
                          padding: "5px 8px",
                          backgroundColor: "#f8f9fa",
                          borderBottom: "1px solid #dee2e6",
                          fontSize: "14px",
                        }}
                      >
                        Bank Balances
                      </td>
                    </tr>
                    {reportData.inflowsData
                      .filter((item) => !item.isTotal)
                      .map((item, idx) => (
                        <tr key={idx}>
                          <td
                            style={{
                              padding: "3px 8px",
                              borderBottom: "1px solid #f0f0f0",
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.account}
                          </td>
                          <td
                            style={{
                              padding: "3px 8px",
                              textAlign: "right",
                              borderBottom: "1px solid #f0f0f0",
                              whiteSpace: "nowrap",
                              fontSize: "14px",
                            }}
                          >
                            {formatNumber(item.amount)}
                          </td>
                        </tr>
                      ))}
                    <tr>
                      <td
                        style={{
                          padding: "5px 8px",
                          fontWeight: "bold",
                          borderTop: "1px solid #000",
                        }}
                      ></td>
                      <td
                        style={{
                          padding: "5px 8px",
                          textAlign: "right",
                          fontWeight: "bold",
                          borderTop: "1px solid #000",
                          whiteSpace: "nowrap",
                          fontSize: "14px",
                        }}
                      >
                        {formatNumber(totalInflows)}
                      </td>
                    </tr>

                    {/* Receivables Section */}
                    <tr>
                      <td
                        colSpan="2"
                        style={{
                          fontWeight: "bold",
                          padding: "5px 8px",
                          backgroundColor: "#f8f9fa",
                          borderBottom: "1px solid #dee2e6",
                          paddingTop: "10px",
                          fontSize: "14px",
                        }}
                      >
                        Receivables
                      </td>
                    </tr>
                    {reportData.receivablesData
                      .filter((item) => !item.isTotal)
                      .map((item, idx) => (
                        <tr key={idx}>
                          <td
                            style={{
                              padding: "3px 8px",
                              borderBottom: "1px solid #f0f0f0",
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.account}
                          </td>
                          <td
                            style={{
                              padding: "3px 8px",
                              textAlign: "right",
                              borderBottom: "1px solid #f0f0f0",
                              whiteSpace: "nowrap",
                              fontSize: "14px",
                            }}
                          >
                            {formatNumber(item.amount)}
                          </td>
                        </tr>
                      ))}
                    <tr>
                      <td
                        style={{
                          padding: "5px 8px",
                          fontWeight: "bold",
                          borderTop: "1px solid #000",
                        }}
                      ></td>
                      <td
                        style={{
                          padding: "5px 8px",
                          textAlign: "right",
                          fontWeight: "bold",
                          borderTop: "1px solid #000",
                          whiteSpace: "nowrap",
                          fontSize: "14px",
                        }}
                      >
                        {formatNumber(totalReceivables)}
                      </td>
                    </tr>

                    {/* Total Inflows */}
                    <tr>
                      <td
                        style={{
                          padding: "8px 8px",
                          fontWeight: "bold",
                          backgroundColor: "#f0f0f0",
                          borderTop: "2px solid #000",
                          fontSize: "14px",
                        }}
                      >
                        TOTAL INFLOWS
                      </td>
                      <td
                        style={{
                          padding: "8px 8px",
                          textAlign: "right",
                          fontWeight: "bold",
                          backgroundColor: "#f0f0f0",
                          borderTop: "2px solid #000",
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatNumber(totalInflows + totalReceivables)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>

              <td style={{ width: "4%" }}></td>

              {/* OUTFLOWS Column */}
              <td
                style={{
                  width: "48%",
                  verticalAlign: "top",
                  paddingLeft: "15px",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th
                        colSpan="2"
                        style={{
                          backgroundColor: "#f0f0f0",
                          padding: "6px 8px",
                          textAlign: "left",
                          fontWeight: "bold",
                          fontSize: "16px",
                          borderBottom: "2px solid #000",
                        }}
                      >
                        OUTFLOWS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Payables Balances Section */}
                    <tr>
                      <td
                        colSpan="2"
                        style={{
                          fontWeight: "bold",
                          padding: "5px 8px",
                          backgroundColor: "#f8f9fa",
                          borderBottom: "1px solid #dee2e6",
                          fontSize: "14px",
                        }}
                      >
                        Payables Balances
                      </td>
                    </tr>
                    {reportData.outflowsData
                      .filter((item) => !item.isTotal)
                      .map((item, idx) => (
                        <tr key={idx}>
                          <td
                            style={{
                              padding: "3px 8px",
                              borderBottom: "1px solid #f0f0f0",
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.account}
                          </td>
                          <td
                            style={{
                              padding: "3px 8px",
                              textAlign: "right",
                              borderBottom: "1px solid #f0f0f0",
                              whiteSpace: "nowrap",
                              fontSize: "14px",
                            }}
                          >
                            {formatNumber(item.amount)}
                          </td>
                        </tr>
                      ))}
                    <tr>
                      <td
                        style={{
                          padding: "5px 8px",
                          fontWeight: "bold",
                          borderTop: "1px solid #000",
                        }}
                      ></td>
                      <td
                        style={{
                          padding: "5px 8px",
                          textAlign: "right",
                          fontWeight: "bold",
                          borderTop: "1px solid #000",
                          whiteSpace: "nowrap",
                          fontSize: "14px",
                        }}
                      >
                        {formatNumber(totalOutflows)}
                      </td>
                    </tr>

                    {/* Active POs & Expenses Section */}
                    <tr>
                      <td
                        colSpan="2"
                        style={{
                          fontWeight: "bold",
                          padding: "5px 8px",
                          backgroundColor: "#f8f9fa",
                          borderBottom: "1px solid #dee2e6",
                          paddingTop: "10px",
                          fontSize: "14px",
                        }}
                      >
                        Active POs & Expenses
                      </td>
                    </tr>
                    {reportData.expensesData
                      .filter((item) => !item.isTotal)
                      .map((item, idx) => (
                        <tr key={idx}>
                          <td
                            style={{
                              padding: "3px 8px",
                              borderBottom: "1px solid #f0f0f0",
                              fontSize: "14px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.account}
                          </td>
                          <td
                            style={{
                              padding: "3px 8px",
                              textAlign: "right",
                              borderBottom: "1px solid #f0f0f0",
                              whiteSpace: "nowrap",
                              fontSize: "14px",
                            }}
                          >
                            {formatNumber(item.amount)}
                          </td>
                        </tr>
                      ))}
                    <tr>
                      <td
                        style={{
                          padding: "5px 8px",
                          fontWeight: "bold",
                          borderTop: "1px solid #000",
                        }}
                      ></td>
                      <td
                        style={{
                          padding: "5px 8px",
                          textAlign: "right",
                          fontWeight: "bold",
                          borderTop: "1px solid #000",
                          whiteSpace: "nowrap",
                          fontSize: "14px",
                        }}
                      >
                        {formatNumber(totalExpenses)}
                      </td>
                    </tr>

                    {/* Estimated Tax Liability */}
                    <tr>
                      <td
                        colSpan="2"
                        style={{
                          fontWeight: "bold",
                          padding: "5px 8px",
                          backgroundColor: "#f8f9fa",
                          borderBottom: "1px solid #dee2e6",
                          fontSize: "14px",
                        }}
                      >
                        Estimated Tax Liability
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "5px 8px", fontSize: "14px" }}>
                        Estimated Tax Liability
                      </td>
                      <td
                        style={{
                          padding: "5px 8px",
                          textAlign: "right",
                          whiteSpace: "nowrap",
                          fontSize: "14px",
                        }}
                      >
                        {formatNumber(estimatedTaxLiability)}
                      </td>
                    </tr>

                    {/* Total Outflows */}
                    <tr>
                      <td
                        style={{
                          padding: "8px 8px",
                          fontWeight: "bold",
                          backgroundColor: "#f0f0f0",
                          borderTop: "2px solid #000",
                          fontSize: "14px",
                        }}
                      >
                        TOTAL OUTFLOWS
                      </td>
                      <td
                        style={{
                          padding: "8px 8px",
                          textAlign: "right",
                          fontWeight: "bold",
                          backgroundColor: "#f0f0f0",
                          borderTop: "2px solid #000",
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatNumber(
                          totalOutflows + totalExpenses + estimatedTaxLiability
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
        {/* Footer */}
        <div
          className="footer"
          style={{
            margin: "0 auto",
            width: "95%",
            marginTop: "5px",
            marginBottom: "15px",
          }}
        >
          <table
            style={{
              width: "100%",
              fontSize: "16px",
            }}
          >
            <tfoot>
              <tr>
                <td
                  style={{ width: "50%", padding: "8px", fontWeight: "bold" }}
                >
                  Total Cash Inflows:
                </td>
                <td
                  style={{
                    width: "50%",
                    padding: "8px",
                    fontWeight: "bold",
                    textAlign: "right",
                  }}
                >
                  Total Cash Outflows:
                </td>
              </tr>

              <tr>
                <td
                  style={{
                    width: "50%",
                    padding: "8px",
                    textAlign: "right",
                    fontWeight: "bold",
                  }}
                >
                  {formatNumber(totalCashInflows)}
                </td>

                <td
                  style={{
                    width: "50%",
                    padding: "8px",
                    textAlign: "right",
                    fontWeight: "bold",
                  }}
                >
                  {formatNumber(totalCashOutflows)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          body {
            margin: 0;
            padding: 0;
            height: 100vh;
          }
          
          .print-container {
            padding: 15px !important;
            max-width: 100% !important;
          }
          
          table {
            page-break-inside: auto;
            width: 100%;! important;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          h2 {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          
          .footer {
            position: fixed;
            bottom: 20px;
            left: 0;
            right: 0;
            background-color: #f8f9fa !important;
            border-top: 1px solid #dee2e6 !important;
            print-color-adjust: exact;
          }
        }
        
        @media screen {
          .print-container {
            background: white;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            margin-top: 0 !important;
          }
        }
        
        @page {
          size: A4;
          margin: 15mm;
        }
        
        * {
          box-sizing: border-box;
        }
        
        html, body, #root {
          height: 100%;
        }
      `}</style>
    </div>
  );
}